import { Lock, CheckCircle2, Clock } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { rp } from "../lib/format";

export const PageHeader = ({ title, subtitle, actions, testid }) => (
  <div className="fade-up mb-5 flex flex-wrap items-center justify-between gap-3">
    <div>
      <h1 data-testid={testid} className="font-heading text-[26px] font-semibold leading-tight text-gray-900 sm:text-[28px]">{title}</h1>
      {subtitle && <p className="mt-1 text-[13px] text-gray-500">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const Money = ({ value, className = "", testid }) => (
  <span data-testid={testid} className={`tabular-nums ${className}`}>{rp(value)}</span>
);

export const EmptyState = ({ icon: Icon, title, desc, testid = "empty-state" }) => (
  <div data-testid={testid} className="relative overflow-hidden rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-12 text-center">
    {Icon && <span className="relative mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50 text-[#E11D5A]"><Icon className="h-7 w-7" /></span>}
    <p className="relative font-heading text-lg font-semibold text-gray-800">{title}</p>
    {desc && <p className="relative mt-1 text-sm text-gray-500">{desc}</p>}
  </div>
);

export const StatusBadge = ({ status, testid = "status-badge", label }) =>
  status === "paid" ? (
    <span data-testid={testid} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
      <CheckCircle2 className="h-3.5 w-3.5" /> {label || "Sudah dibayar"}
    </span>
  ) : (
    <span data-testid={testid} className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 ring-1 ring-inset ring-amber-200">
      <Clock className="h-3.5 w-3.5" /> {label || "Belum dibayar"}
    </span>
  );

const KH = {
  terverifikasi: ["Terverifikasi", "bg-emerald-100 text-emerald-800"],
  perlu_dicek: ["Perlu dicek", "bg-amber-100 text-amber-800"],
  disetujui: ["Disetujui", "bg-sky-100 text-sky-800"],
  ditolak: ["Ditolak", "bg-red-100 text-red-700"],
};

export const KehadiranBadge = ({ status, testid }) => {
  const [label, cls] = KH[status] || [status, "bg-gray-100 text-gray-700"];
  return <span data-testid={testid} className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-xs font-semibold ${cls}`}>{label}</span>;
};

const PB = {
  dipinjam: ["Dipinjam", "bg-amber-100 text-amber-800"],
  dikembalikan: ["Dikembalikan", "bg-emerald-100 text-emerald-800"],
  menunggu: ["Menunggu Cek", "bg-sky-100 text-sky-800"],
  ditolak: ["Ditolak", "bg-red-100 text-red-700"],
};

export const PeralatanBadge = ({ status, testid }) => {
  const [label, cls] = PB[status] || PB.dipinjam;
  return <span data-testid={testid} className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-xs font-semibold ${cls}`}>{label}</span>;
};

export const PeralatanMeta = ({ p }) => (
  <p className="text-[11px] text-gray-400">
    Diisi oleh {p.diisi_oleh === "anak" ? "Anak" : "Admin"}
    {p.dikonfirmasi_at && ` · dicek ${p.dikonfirmasi_oleh || "Admin"} ${new Date(p.dikonfirmasi_at).toLocaleString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}`}
  </p>
);

const KB = {
  gold: "bg-amber-100 text-amber-900 ring-amber-300",
  platinum: "bg-slate-200 text-slate-800 ring-slate-400",
};

export const KategoriBadge = ({ kategori, testid }) => !KB[kategori] ? null : (
  <span data-testid={testid} className={`inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1 ring-inset ${KB[kategori] || "bg-red-50 text-red-700 ring-red-200"}`}>
    {kategori === "gold" ? "Gold" : kategori === "platinum" ? "Platinum" : "Belum diatur"}
  </span>
);

export const LockedTag = ({ testid }) => (
  <span data-testid={testid} title="Periode sudah dibayar" className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-500">
    <Lock className="h-3 w-3" /> Terkunci
  </span>
);

export const ConfirmDialog = ({ open, onOpenChange, title, description, confirmLabel = "Ya, lanjutkan", onConfirm, destructive, testid = "confirm-dialog" }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent data-testid={testid}>
      <AlertDialogHeader>
        <AlertDialogTitle className="font-heading">{title}</AlertDialogTitle>
        {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid={`${testid}-cancel`} className="h-10">Batal</AlertDialogCancel>
        <AlertDialogAction
          data-testid={`${testid}-confirm`}
          onClick={onConfirm}
          className={`h-10 ${destructive ? "bg-red-600 hover:bg-red-700" : ""}`}
        >
          {confirmLabel}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <label className="text-[13px] font-medium text-gray-700">{label}</label>
    {children}
    {hint && <p className="text-xs text-gray-500">{hint}</p>}
  </div>
);
